import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

/**
 * A titled block on a connection page.
 *
 * Replaces the tab bar on the connection surface. Tabs are peer alternatives —
 * they need every panel to have content for every instance and the set to be
 * stable. Neither holds here: a chat connection has no sync runs and no
 * operations, so two of four tabs render empty states, and the tab set itself
 * had to swap a label ("Feeds" / "Channels") to stop the bar resizing. Sections
 * scroll past when they are empty and cost nothing when they are not.
 */
export function PageSection({
	id,
	title,
	count,
	action,
	muted,
	children,
}: {
	/** Anchor target, so a sibling section can link to this one. */
	id?: string;
	title: string;
	/** Short qualifier beside the title — "3 sources", "stalled", "1 tool". */
	count?: string;
	/** Right-aligned control belonging to this section. */
	action?: ReactNode;
	/** Dim the heading when the section is present but not currently usable. */
	muted?: boolean;
	children: ReactNode;
}) {
	return (
		<section id={id} className="min-w-0 scroll-mt-24">
			<div className="mb-2.5 flex items-center justify-between gap-3 border-b pb-2">
				<h2
					className={cn(
						"text-[13px] font-semibold tracking-[-0.005em]",
						muted && "text-muted-foreground",
					)}
				>
					{title}
					{count ? (
						<span className="ml-1.5 text-[11.5px] font-normal text-muted-foreground">
							{count}
						</span>
					) : null}
				</h2>
				{action}
			</div>
			{children}
		</section>
	);
}

/**
 * A section that starts closed. The summary stays visible while collapsed so
 * the shut state still says what is inside — a bare "Settings ›" makes the
 * reader open it to find out whether they care.
 */
export function CollapsibleSection({
	title,
	summary,
	open,
	onOpenChange,
	children,
}: {
	title: string;
	summary: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	children: ReactNode;
}) {
	return (
		<Collapsible open={open} onOpenChange={onOpenChange} className="min-w-0">
			{/* Radix owns the trigger so the disclosure reports aria-expanded and
			    aria-controls. A hand-rolled button with its own onClick renders
			    the caret and nothing a screen reader can use. */}
			<CollapsibleTrigger className="group flex w-full items-center gap-2 border-b pb-2 text-left transition-colors hover:border-muted-foreground">
				<ChevronRight
					className={cn(
						"size-3 shrink-0 text-muted-foreground transition-transform",
						open && "rotate-90",
					)}
				/>
				<span className="text-[13px] font-semibold tracking-[-0.005em]">
					{title}
				</span>
				<span className="ml-auto truncate text-[11.5px] text-muted-foreground transition-colors group-hover:text-foreground">
					{open ? "Hide" : summary}
				</span>
			</CollapsibleTrigger>
			<CollapsibleContent className="pt-3">{children}</CollapsibleContent>
		</Collapsible>
	);
}
